import React, { useMemo, useRef, useEffect, useState } from 'react';
import { TrendingUp, Zap, Users2, ChevronDown, ChevronRight, Upload } from 'lucide-react';
import Hover3DCard from './Hover3DCard';
import LayerPanel from './LayerPanel';
import FileUpload from './FileUpload';

const SOLAR_CATS = [
    { key: 'Gold', color: '#D97706', bg: '#FFFBEB', border: '#FDE68A', icon: '☀️', label: 'Gold', desc: 'Ideal conditions' },
    { key: 'Silver', color: '#2563EB', bg: '#EFF6FF', border: '#BFDBFE', icon: '💧', label: 'Silver', desc: 'High sun, heat risk' },
    { key: 'Moderate', color: '#C2410C', bg: '#FFF7ED', border: '#FED7AA', icon: '⚡', label: 'Moderate', desc: 'Standard gear OK' },
    { key: 'Poor', color: '#DC2626', bg: '#FEF2F2', border: '#FECACA', icon: '⚠️', label: 'Poor', desc: 'Low ROI zone' },
    { key: 'Unknown', color: '#6B7280', bg: '#F9FAFB', border: '#E5E7EB', icon: '❓', label: 'Unknown', desc: 'No data' },
];

const AnimatedNumber = ({ value }) => {
    const ref = useRef(null);
    useEffect(() => {
        if (!ref.current) return;
        const el = ref.current;
        const end = value;
        const start = parseInt(el.textContent.replace(/,/g, '')) || 0;
        const dur = 500;
        const step = (end - start) / (dur / 16);
        let cur = start;
        const timer = setInterval(() => {
            cur += step;
            if ((step > 0 && cur >= end) || (step < 0 && cur <= end)) { cur = end; clearInterval(timer); }
            el.textContent = Math.round(cur).toLocaleString();
        }, 16);
        return () => clearInterval(timer);
    }, [value]);
    return <span ref={ref}>{value.toLocaleString()}</span>;
};

const ProgressBar = ({ pct, color }) => (
    <div style={{ height: 4, background: '#E5EDDB', borderRadius: 10, overflow: 'hidden', marginTop: 5 }}>
        <div style={{
            width: `${Math.min(pct, 100)}%`, height: '100%',
            background: color, borderRadius: 10,
            transition: 'width 0.6s cubic-bezier(0.16,1,0.3,1)',
        }} />
    </div>
);

const StatsPanel = ({ isOpen, onClose, data, filteredData, solarData, viewMode, setViewMode, selectedClient, showUpload, setShowUpload, onDataLoaded }) => {
    const [clientsOpen, setClientsOpen] = useState(false);

    const clientStats = useMemo(() => {
        const acc = {};
        data.forEach(s => { const c = (s.client_name || 'General').trim(); acc[c] = (acc[c] || 0) + 1; });
        return Object.entries(acc).sort((a, b) => b[1] - a[1]);
    }, [data]);

    const maxClientCount = clientStats[0]?.[1] || 1;

    const clientColors = useMemo(() => {
        const palette = ['#76AB3F', '#2563EB', '#D97706', '#7C3AED', '#059669', '#DB2777', '#EA580C', '#0284C7', '#9333EA', '#16A34A'];
        const map = {};
        clientStats.forEach(([name], i) => { map[name] = palette[i % palette.length]; });
        return map;
    }, [clientStats]);

    const clientActualColors = useMemo(() => {
        const map = {};
        data.forEach(s => {
            const name = (s.client_name || 'General').trim();
            if (!map[name] && (s.Color || s.color)) map[name] = s.Color || s.color;
        });
        return map;
    }, [data]);

    const solarCatCounts = useMemo(() => {
        if (!solarData || solarData.size === 0) return {};
        const acc = {};
        for (const info of solarData.values()) { const cat = info.category || 'Unknown'; acc[cat] = (acc[cat] || 0) + 1; }
        return acc;
    }, [solarData]);

    const totalSolar = Object.values(solarCatCounts).reduce((a, b) => a + b, 0);

    return (
        <aside
            className="absolute top-0 right-0 bottom-0 bg-white border-l border-[#E5EDDB] flex flex-col z-[1000] w-full md:w-[300px] shadow-[-4px_0_24px_rgba(45,92,18,0.12)] transition-transform duration-300"
            style={{ transform: isOpen ? 'translateX(0)' : 'translateX(100%)' }}
        >
            {/* Header */}
            <div style={{
                padding: '16px 18px',
                background: 'linear-gradient(135deg, #76AB3F, #76AB3F)',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}>
                <div>
                    <p style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.14em', color: 'rgba(255,255,255,0.6)', textTransform: 'uppercase', margin: '0 0 3px' }}>
                        Site Intelligence
                    </p>
                    <p style={{ fontSize: 22, fontWeight: 800, color: '#fff', lineHeight: 1, margin: 0 }}>
                        <AnimatedNumber value={data.length} />
                        <span style={{ fontSize: 12, fontWeight: 400, color: 'rgba(255,255,255,0.55)', marginLeft: 6 }}>total sites</span>
                    </p>
                </div>
                <button
                    onClick={onClose}
                    style={{
                        width: 30, height: 30, borderRadius: 8, display: 'flex',
                        alignItems: 'center', justifyContent: 'center',
                        background: 'rgba(255,255,255,0.12)',
                        border: '1px solid rgba(255,255,255,0.2)',
                        color: 'rgba(255,255,255,0.8)', cursor: 'pointer',
                        transition: 'all 0.15s',
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.22)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.12)'}
                >
                    <ChevronRight size={16} />
                </button>
            </div>

            {/* Quick stats row */}
            <div style={{ padding: '12px 14px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, background: '#F4FBF0', borderBottom: '1px solid #E5EDDB' }}>
                {[
                    { icon: <Users2 size={13} />, label: 'Clients', value: clientStats.length, color: '#4a7a28' },
                    { icon: <TrendingUp size={13} />, label: 'Showing', value: filteredData.length, color: '#2563EB' },
                ].map(({ icon, label, value, color }) => (
                    <Hover3DCard key={label} color={color} style={{ borderRadius: 10 }}>
                        <div style={{ padding: '9px 11px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 5, color, marginBottom: 2 }}>
                                {icon}
                                <span style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em' }}>{label}</span>
                            </div>
                            <span style={{ fontSize: 20, fontWeight: 800, color: '#1a3a08' }}>
                                <AnimatedNumber value={value} />
                            </span>
                        </div>
                    </Hover3DCard>
                ))}
            </div>

            {/* Scrollable body */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '14px' }} className="custom-scrollbar">

                {/* Solar distribution */}
                {viewMode === 'solar' && solarData && solarData.size > 0 && (
                    <section style={{ marginBottom: 18 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
                            <Zap size={12} style={{ color: '#D97706' }} />
                            <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#D97706' }}>Solar Distribution</span>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                            {SOLAR_CATS.filter(c => solarCatCounts[c.key] > 0).map(cat => {
                                const count = solarCatCounts[cat.key] || 0;
                                const pct = totalSolar > 0 ? (count / totalSolar) * 100 : 0;
                                return (
                                    <div key={cat.key} style={{
                                        background: cat.bg, border: `1px solid ${cat.border}`,
                                        borderRadius: 10, padding: '9px 11px',
                                    }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                                                <span style={{ fontSize: 14 }}>{cat.icon}</span>
                                                <div>
                                                    <div style={{ fontSize: 12, fontWeight: 700, color: cat.color }}>{cat.label}</div>
                                                    <div style={{ fontSize: 10, color: '#6b7280' }}>{cat.desc}</div>
                                                </div>
                                            </div>
                                            <div style={{ textAlign: 'right' }}>
                                                <div style={{ fontSize: 15, fontWeight: 800, color: '#1a3a08' }}>{count}</div>
                                                <div style={{ fontSize: 10, color: '#9ca3af' }}>{pct.toFixed(0)}%</div>
                                            </div>
                                        </div>
                                        <ProgressBar pct={pct} color={cat.color} />
                                    </div>
                                );
                            })}
                        </div>
                    </section>
                )}

                {/* Client breakdown - collapsible */}
                <section>
                    <button
                        onClick={() => setClientsOpen(prev => !prev)}
                        style={{
                            display: 'flex', alignItems: 'center', gap: 6,
                            width: '100%', background: 'none', border: 'none',
                            cursor: 'pointer', padding: '6px 0', marginBottom: clientsOpen ? 10 : 0,
                        }}
                    >
                        {clientsOpen
                            ? <ChevronDown size={12} style={{ color: '#4a7a28' }} />
                            : <ChevronRight size={12} style={{ color: '#4a7a28' }} />
                        }
                        <Users2 size={12} style={{ color: '#4a7a28' }} />
                        <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#4a7a28' }}>Clients</span>
                        <span style={{ marginLeft: 'auto', fontSize: 10, fontWeight: 600, color: '#9ca3af' }}>{clientStats.length}</span>
                    </button>
                    {clientsOpen && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                            {clientStats.map(([name, count]) => {
                                const pct = (count / maxClientCount) * 100;
                                const color = clientActualColors[name] || clientColors[name];
                                const isActive = selectedClient === name.toUpperCase() || selectedClient === 'All Clients';
                                return (
                                    <div key={name} style={{
                                        padding: '7px 10px', borderRadius: 9,
                                        background: isActive ? '#F4FBF0' : 'transparent',
                                        border: isActive ? '1px solid #C7E0A8' : '1px solid transparent',
                                        transition: 'all 0.15s',
                                    }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                            <div style={{
                                                width: 8, height: 8, borderRadius: '50%',
                                                background: color, flexShrink: 0,
                                                boxShadow: `0 0 5px ${color}60`,
                                            }} />
                                            <span style={{
                                                flex: 1, fontSize: 12, fontWeight: 500, color: '#374151',
                                                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                            }}>{name}</span>
                                            <span style={{
                                                fontSize: 11, fontWeight: 700, color: '#4a7a28',
                                                background: '#E8F5D9', borderRadius: 5,
                                                padding: '1px 6px',
                                            }}>{count}</span>
                                        </div>
                                        <ProgressBar pct={pct} color={color} />
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </section>

                {/* Map Layers - inline */}
                <LayerPanel viewMode={viewMode} setViewMode={setViewMode} inline />

                {/* Upload Section */}
                <div style={{
                    marginTop: '15px', paddingTop: '15px', borderTop: '1px solid #E5EDDB',
                }}>
                    <button
                        onClick={() => setShowUpload(v => !v)}
                        style={{
                            display: 'flex', alignItems: 'center', gap: 8,
                            width: '100%', padding: '10px 14px', borderRadius: 10,
                            cursor: 'pointer', fontSize: 12, fontWeight: 600,
                            fontFamily: 'Inter, sans-serif', transition: 'all 0.18s',
                            background: showUpload ? '#76AB3F' : '#F4FBF0',
                            border: showUpload ? '1px solid #76AB3F' : '1px solid #C7E0A8',
                            color: showUpload ? '#fff' : '#4a7a28',
                        }}
                        onMouseEnter={e => { if (!showUpload) e.currentTarget.style.background = '#E8F5D9'; }}
                        onMouseLeave={e => { if (!showUpload) e.currentTarget.style.background = '#F4FBF0'; }}
                    >
                        <Upload size={14} />
                        <span>{showUpload ? 'Back to Map' : 'Upload Sites'}</span>
                    </button>
                    {showUpload && (
                        <div style={{ marginTop: 12 }}>
                            <FileUpload onDataLoaded={onDataLoaded} />
                        </div>
                    )}
                </div>
            </div>

            {/* Footer */}
            <div style={{
                padding: '10px 16px',
                borderTop: '1px solid #E5EDDB',
                background: '#F4FBF0',
                fontSize: 10, color: '#9ca3af',
                textAlign: 'center', fontWeight: 500,
            }}>
                AGS Geography·{new Date().getFullYear()}
            </div>
        </aside>
    );
};

export default StatsPanel;
